/* eslint-disable react/prop-types */
import PropTypes from 'prop-types';
import ReactSelect, { createFilter } from 'react-select';
import Tooltip from 'components/Tooltip';
import get from 'lodash/get';
import { Label } from 'components/Typography';
import {
  Container,
  Value,
  CaretIcon,
  CheckIcon,
  ErrorMessage,
  Placeholder
} from './styled';
import { getSelectStyles, defaultTheme } from './variants';

// value and onChange should only be passed
// in if formik is not used
const Select = ({
  options,
  variant,
  placeholder,
  components,
  label,
  tooltip,
  formik,
  value,
  onChange,
  name,
  disabled,
  className,
  disableErrorMessage = false,
  newInputStyle = false,
  isSearchable = true,
  hideLabel = false
}) => {
  const renderLabel = (
    <Label htmlFor={name} variant={variant}>
      {label}
      {tooltip && <Tooltip variant={variant}>{tooltip}</Tooltip>}
      {formik && !disableErrorMessage && (
        <>
          {get(formik.touched, name) && get(formik.errors, name) && (
            <ErrorMessage>{get(formik.errors, name)}</ErrorMessage>
          )}
          {!get(formik.errors, name) && get(formik.touched, name) && formik.dirty && (
            <CheckIcon />
          )}
        </>
      )}
    </Label>
  );

  const handleChange = (v) => {
    if (formik) {
      formik.setFieldTouched(name, true, true);
      formik.setFieldValue(name, v.value);
    } else {
      onChange(v);
    }
  };

  const handleBlur = () =>
    formik && formik.setTouched({ ...formik.touched, [name]: true });

  const selectedValue = formik
    ? options.filter((option) => option.value === get(formik.values, name))
    : options.filter((option) => option.value === value);

  return (
    <Container className={className}>
      {(label || formik) && !hideLabel && renderLabel}
      <ReactSelect
        value={selectedValue}
        filterOption={createFilter({ ignoreAccents: false })}
        onChange={handleChange}
        onBlur={handleBlur}
        options={options}
        components={{
          DropdownIndicator: () => (disabled ? null : <CaretIcon variant={variant} />),
          SingleValue: ({ data }) => {
            return (
              <Value variant={variant} data={data} disabled={disabled}>
                {data.label}
              </Value>
            );
          },
          Placeholder: () => {
            return (
              <Placeholder variant={variant} isPlaceholder>
                {placeholder}
              </Placeholder>
            );
          },
          ...components
        }}
        isDisabled={disabled}
        styles={getSelectStyles(variant, disabled, newInputStyle)}
        theme={(theme) => defaultTheme(theme)}
        isSearchable={isSearchable}
      />
    </Container>
  );
};

Select.propTypes = {
  options: PropTypes.array.isRequired,
  variant: PropTypes.oneOf([
    'block',
    'light',
    'dark',
    'global',
    'small',
    'newRH',
    'bordered'
  ]),
  placeholder: PropTypes.string,
  components: PropTypes.object,
  label: PropTypes.string,
  tooltip: PropTypes.string,
  formik: PropTypes.object,
  value: PropTypes.oneOfType([PropTypes.object, PropTypes.string, PropTypes.number]),
  onChange: PropTypes.func,
  name: PropTypes.string,
  disabled: PropTypes.bool,
  className: PropTypes.string,
  isSearchable: PropTypes.bool,
  hideLabel: PropTypes.bool
};

Select.defaultProps = {
  variant: 'dark',
  placeholder: '',
  components: {},
  label: '',
  tooltip: '',
  formik: null,
  value: null,
  onChange: () => {},
  name: '',
  disabled: false,
  className: '',
  isSearchable: true,
  hideLabel: false
};

export default Select;
